import React, { useState } from 'react';
import { Send, Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    nom: '',
    organisation: '',
    fonction: '',
    email: '',
    telephone: '',
    natureRequest: [],
    message: '',
    paysConcernes: '',
    timeline: '',
    consentement: false,
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNatureChange = (optionId, checked) => {
    setFormData((prev) => ({
      ...prev,
      natureRequest: checked
        ? [...prev.natureRequest, optionId]
        : prev.natureRequest.filter((id) => id !== optionId),
    }));
  };

  const handleTimelineChange = (optionId) => {
    setFormData((prev) => ({
      ...prev,
      timeline: prev.timeline === optionId ? '' : optionId,
    }));
  };

  const handleConsentChange = (checked) => {
    setFormData((prev) => ({ ...prev, consentement: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Validation
    if (!formData.nom || !formData.organisation || !formData.email || !formData.message || !formData.consentement) {
      setStatus({ type: 'error', message: t.contact.errorRequired });
      setIsSubmitting(false);
      return;
    }

    if (formData.natureRequest.length === 0) {
      setStatus({ type: 'error', message: t.contact.errorNature });
      setIsSubmitting(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({ type: 'error', message: t.contact.errorEmail });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        nom: formData.nom,
        organisation: formData.organisation,
        fonction: formData.fonction,
        email: formData.email,
        telephone: formData.telephone,
        nature_request: formData.natureRequest,
        message: formData.message,
        pays_concernes: formData.paysConcernes,
        timeline: formData.timeline,
      });

      if (response.data.success) {
        setStatus({ type: 'success', message: t.contact.success });
        setFormData({
          nom: '',
          organisation: '',
          fonction: '',
          email: '',
          telephone: '',
          natureRequest: [],
          message: '',
          paysConcernes: '',
          timeline: '',
          consentement: false,
        });
      } else {
        throw new Error(response.data.message || 'Error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        type: 'error',
        message: error.response?.data?.detail || t.contact.errorGeneral,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#302f2c] py-20 md:py-28">
      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left column - Info */}
          <div className="lg:col-span-2">
            <h2 className="font-black text-[#d9fb06] text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.85] mb-6">
              {t.contact.title}
            </h2>

            <div className="w-20 h-20 rounded-full border border-[#3f4816] flex items-center justify-center mb-6">
              <Mail className="w-8 h-8 text-[#d9fb06]" />
            </div>

            <p className="text-[#888680] text-sm md:text-base leading-relaxed mb-6 max-w-md">
              {t.contact.disclaimer}
            </p>
          </div>

          {/* Right column - Form */}
          <div className="lg:col-span-3 bg-[#1a1c1b] border border-[#3f4816]/50 p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Row 1: Nom & Organisation */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="nom" className="text-[#d9fb06] font-medium text-sm">
                    {t.contact.fields.nom} <span className="text-[#888680]">*</span>
                  </Label>
                  <Input
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className="bg-transparent border-[#3f4816] text-[#d9fb06] placeholder:text-[#888680]/50 focus:border-[#d9fb06] rounded-none h-11"
                    placeholder={t.contact.placeholders.nom}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organisation" className="text-[#d9fb06] font-medium text-sm">
                    {t.contact.fields.organisation} <span className="text-[#888680]">*</span>
                  </Label>
                  <Input
                    id="organisation"
                    name="organisation"
                    value={formData.organisation}
                    onChange={handleChange}
                    className="bg-transparent border-[#3f4816] text-[#d9fb06] placeholder:text-[#888680]/50 focus:border-[#d9fb06] rounded-none h-11"
                    placeholder={t.contact.placeholders.organisation}
                  />
                </div>
              </div>

              {/* Row 2: Fonction & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="fonction" className="text-[#d9fb06] font-medium text-sm">
                    {t.contact.fields.fonction}
                  </Label>
                  <Input
                    id="fonction"
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleChange}
                    className="bg-transparent border-[#3f4816] text-[#d9fb06] placeholder:text-[#888680]/50 focus:border-[#d9fb06] rounded-none h-11"
                    placeholder={t.contact.placeholders.fonction}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#d9fb06] font-medium text-sm">
                    {t.contact.fields.email} <span className="text-[#888680]">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-transparent border-[#3f4816] text-[#d9fb06] placeholder:text-[#888680]/50 focus:border-[#d9fb06] rounded-none h-11"
                    placeholder={t.contact.placeholders.email}
                  />
                </div>
              </div>

              {/* Row 3: Téléphone & Pays */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="telephone" className="text-[#d9fb06] font-medium text-sm">
                    {t.contact.fields.telephone}
                  </Label>
                  <Input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="bg-transparent border-[#3f4816] text-[#d9fb06] placeholder:text-[#888680]/50 focus:border-[#d9fb06] rounded-none h-11"
                    placeholder={t.contact.placeholders.telephone}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paysConcernes" className="text-[#d9fb06] font-medium text-sm">
                    {t.contact.fields.paysConcernes}
                  </Label>
                  <Input
                    id="paysConcernes"
                    name="paysConcernes"
                    value={formData.paysConcernes}
                    onChange={handleChange}
                    className="bg-transparent border-[#3f4816] text-[#d9fb06] placeholder:text-[#888680]/50 focus:border-[#d9fb06] rounded-none h-11"
                    placeholder={t.contact.placeholders.paysConcernes}
                  />
                </div>
              </div>

              {/* Nature de la demande */}
              <div className="space-y-3">
                <Label className="text-[#d9fb06] font-medium text-sm block">
                  {t.contact.fields.natureRequest} <span className="text-[#888680]">*</span>
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {t.contact.natureOptions.map((option) => (
                    <div key={option.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={option.id}
                        checked={formData.natureRequest.includes(option.id)}
                        onCheckedChange={(checked) => handleNatureChange(option.id, checked)}
                        className="border-[#3f4816] data-[state=checked]:bg-[#d9fb06] data-[state=checked]:border-[#d9fb06] data-[state=checked]:text-[#1a1c1b]"
                      />
                      <Label
                        htmlFor={option.id}
                        className="text-[#888680] text-sm font-normal cursor-pointer hover:text-[#d9fb06] transition-colors"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Échéance */}
              <div className="space-y-3">
                <Label className="text-[#d9fb06] font-medium text-sm block">
                  {t.contact.fields.timeline}
                </Label>
                <div className="flex flex-wrap gap-4">
                  {t.contact.timelineOptions.map((option) => (
                    <div key={option.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={`timeline-${option.id}`}
                        checked={formData.timeline === option.id}
                        onCheckedChange={() => handleTimelineChange(option.id)}
                        className="border-[#3f4816] data-[state=checked]:bg-[#d9fb06] data-[state=checked]:border-[#d9fb06] data-[state=checked]:text-[#1a1c1b]"
                      />
                      <Label
                        htmlFor={`timeline-${option.id}`}
                        className="text-[#888680] text-sm font-normal cursor-pointer hover:text-[#d9fb06] transition-colors"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-[#d9fb06] font-medium text-sm">
                  {t.contact.fields.message} <span className="text-[#888680]">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="bg-transparent border-[#3f4816] text-[#d9fb06] placeholder:text-[#888680]/50 focus:border-[#d9fb06] rounded-none resize-none"
                  placeholder={t.contact.placeholders.message}
                />
              </div>

              {/* Consentement */}
              <div className="flex items-start space-x-3 pt-1">
                <Checkbox
                  id="consentement"
                  checked={formData.consentement}
                  onCheckedChange={handleConsentChange}
                  className="border-[#3f4816] data-[state=checked]:bg-[#d9fb06] data-[state=checked]:border-[#d9fb06] data-[state=checked]:text-[#1a1c1b] mt-0.5"
                />
                <Label
                  htmlFor="consentement"
                  className="text-[#888680] text-sm font-normal cursor-pointer hover:text-[#d9fb06]/80 transition-colors leading-relaxed"
                >
                  {t.contact.fields.consentement} <span className="text-[#d9fb06]">*</span>
                </Label>
              </div>

              {/* Status message */}
              {status.message && (
                <div
                  className={`flex items-start gap-3 p-4 rounded-none ${
                    status.type === 'success'
                      ? 'bg-[#3f4816]/30 border border-[#d9fb06]/30'
                      : 'bg-red-900/20 border border-red-500/30'
                  }`}
                >
                  {status.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <p className={`text-sm leading-relaxed ${status.type === 'success' ? 'text-[#d9fb06]' : 'text-red-400'}`}>
                    {status.message}
                  </p>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#d9fb06] text-[#1a1c1b] rounded-full py-5 font-semibold text-sm uppercase tracking-wide hover:bg-[#d9fb06]/90 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.contact.submitting}
                  </>
                ) : (
                  <>
                    {t.contact.submit}
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
